import { showNewMessage } from '@intercom/messenger-js-sdk';
import { Button, Field, FieldLabel, InputEnd, InputStart } from '@koyeb/design-system';
import { CardCvcElement, CardExpiryElement, CardNumberElement, useStripe } from '@stripe/react-stripe-js';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { Controller, FormState, useForm } from 'react-hook-form';

import { apiMutation, useInvalidateApiQuery, useOrganization, useUser } from 'src/api';
import { withStopPropagation } from 'src/application/dom-events';
import { notify } from 'src/application/notify';
import { StripeProvider } from 'src/application/stripe';
import { FormValues, handleSubmit, useFormErrorHandler } from 'src/hooks/form';
import { usePaymentMethodMutation } from 'src/hooks/stripe';
import { useThemeModeOrPreferred } from 'src/hooks/theme';
import { Translate, createTranslate } from 'src/intl/translate';
import { Address, OrganizationPlan } from 'src/model';

import { AddressField } from './address-field/address-field';
import { CloseDialogButton, Dialog, DialogFooter, DialogHeader, closeDialog } from './dialog';
import { ControlledInput } from './forms';

const T = createTranslate('components.upgradeDialog');

const classes = {
  base: clsx(
    'col h-10 w-full justify-center rounded-sm border px-2 -outline-offset-1',
    'bg-neutral', // todo: make sure it works with all instances of the payment form
  ),
  focus: clsx('focused'),
};

const stylesLight = {
  base: {
    color: 'rgb(9 9 11)',
    '::placeholder': {
      color: 'rgb(113 113 122)',
    },
  },
};

const stylesDark = {
  base: {
    color: 'rgb(250 250 250)',
    '::placeholder': {
      color: 'rgb(161 161 170)',
    },
  },
};

type PaymentFormProps = {
  plan: OrganizationPlan;
  onPlanChanged?: () => void;
  renderFooter: (formState: FormState<{ address: Address }>) => React.ReactNode;
};

export function PaymentForm({ plan, onPlanChanged, renderFooter }: PaymentFormProps) {
  const t = T.useTranslate();

  const user = useUser();
  const organization = useOrganization();

  const invalidate = useInvalidateApiQuery();

  const form = useForm<{ billingAlertAmount: number; address: Address }>({
    defaultValues: {
      billingAlertAmount: 20,
      address: organization?.billing.address ?? {
        line1: '',
        postalCode: '',
        city: '',
        country: '',
      },
    },
  });

  const billingInfoMutation = useMutation({
    ...apiMutation('patch /v1/organizations/{id}', (address: Address) => ({
      path: { id: organization!.id },
      query: {},
      body: {
        address1: address.line1,
        address2: address.line2,
        city: address.city,
        postal_code: address.postalCode,
        state: address.state,
        country: address.country,
        billing_name: organization?.billing.name === undefined ? user?.name : undefined,
        billing_email: organization?.billing.email === undefined ? user?.email : undefined,
      },
    })),
    onError: useFormErrorHandler(form, (error) => ({
      'address.line1': error.address1,
      'address.line2': error.address2,
      'address.city': error.city,
      'address.postalCode': error.postal_code,
      'address.state': error.state,
      'address.country': error.country,
    })),
  });

  const changePlanMutation = useMutation({
    ...apiMutation('post /v1/organizations/{id}/plan', (plan: OrganizationPlan) => ({
      path: { id: organization!.id },
      body: { plan },
    })),
    async onSuccess() {
      await Promise.all([
        invalidate('get /v1/account/organization'),
        invalidate('get /v1/organizations/{organization_id}/quotas'),
        invalidate('get /v1/subscriptions/{id}'),
      ]);

      onPlanChanged?.();
    },
  });

  const paymentMethodMutation = usePaymentMethodMutation({
    onTimeout: () => notify.error(<PaymentMethodTimeout />),
  });

  const updateBudgetMutation = useMutation({
    ...apiMutation('put /v1/organizations/{organization_id}/budget', (amount: number) => ({
      path: { organization_id: organization!.id },
      body: { amount: String(100 * amount) },
    })),
  });

  const onSubmit = async ({ billingAlertAmount, address }: FormValues<typeof form>) => {
    await billingInfoMutation.mutateAsync(address);
    await paymentMethodMutation.mutateAsync();
    await changePlanMutation.mutateAsync(plan);

    if (!Number.isNaN(billingAlertAmount)) {
      await updateBudgetMutation.mutateAsync(billingAlertAmount);
    }
  };

  return (
    <form onSubmit={withStopPropagation(handleSubmit(form, onSubmit))} className="col gap-4">
      <PaymentFormFields />

      <Controller
        control={form.control}
        name="address"
        render={({ field, fieldState }) => (
          <AddressField
            {...field}
            required
            size={3}
            label={<T id="addressLabel" />}
            placeholder={t('addressPlaceholder')}
            errors={fieldState.error}
          />
        )}
      />

      <p className="text-dim">
        {plan === 'starter' && <T id="temporaryHoldMessage" />}
        {plan !== 'starter' && <T id="proratedChargeMessage" />}
      </p>

      <ControlledInput
        control={form.control}
        name="billingAlertAmount"
        type="number"
        label={<T id="billingAlert.label" />}
        start={
          <InputStart>
            <T id="billingAlert.inputStart" />
          </InputStart>
        }
        end={
          <InputEnd>
            <T id="billingAlert.inputEnd" />
          </InputEnd>
        }
        className="max-w-48"
      />

      <p className="text-dim">
        <T id="billingAlert.description" />
      </p>

      {renderFooter(form.formState)}
    </form>
  );
}

export function PaymentFormFields() {
  const stripe = useStripe();

  const theme = useThemeModeOrPreferred();
  const style = theme === 'light' ? stylesLight : stylesDark;

  if (stripe === null) {
    return <T id="loadingStripe" />;
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <Field className="col-span-2">
        <FieldLabel>
          <T id="cardNumberLabel" />
        </FieldLabel>
        <CardNumberElement options={{ classes, style }} />
      </Field>

      <Field className="flex-1">
        <FieldLabel>
          <T id="expirationLabel" />
        </FieldLabel>
        <CardExpiryElement options={{ classes, style }} />
      </Field>

      <Field className="flex-1">
        <FieldLabel>
          <T id="cvcLabel" />
        </FieldLabel>
        <CardCvcElement options={{ classes, style }} />
      </Field>
    </div>
  );
}

export type UpgradeDialogProps = {
  plan?: OrganizationPlan;
  onPlanChanged?: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  submit?: React.ReactNode;
};

export function UpgradeDialog() {
  return (
    <Dialog id="Upgrade" className="col w-full max-w-xl gap-4">
      {(props) => <UpgradeDialogContent {...props} />}
    </Dialog>
  );
}

function UpgradeDialogContent({
  plan = 'starter',
  onPlanChanged,
  title,
  description,
  submit,
}: UpgradeDialogProps) {
  return (
    <>
      <DialogHeader title={title ?? <T id="title" />} />

      {description && <p className="text-dim">{description}</p>}

      <StripeProvider>
        <PaymentForm
          plan={plan}
          onPlanChanged={() => {
            closeDialog();
            onPlanChanged?.();
          }}
          renderFooter={(formState) => (
            <DialogFooter>
              <CloseDialogButton size={3}>
                <Translate id="common.cancel" />
              </CloseDialogButton>

              <Button type="submit" size={3} loading={formState.isSubmitting}>
                {submit ?? <T id="submit" />}
              </Button>
            </DialogFooter>
          )}
        />
      </StripeProvider>
    </>
  );
}

export function PaymentMethodTimeout() {
  return (
    <div className="col gap-1">
      <strong>
        <T id="paymentMethodTimeoutTitle" />
      </strong>

      <p>
        <T
          id="paymentMethodTimeoutDescription"
          values={{
            contactUs: (children) => (
              <button type="button" className="underline" onClick={() => showNewMessage('')}>
                {children}
              </button>
            ),
          }}
        />
      </p>
    </div>
  );
}
